// components
import { FilterPopper } from 'components/flex-portal/FlexMatch/components/FilterPopper';
import { ToolbarWrapper } from 'components/shared/ToolbarSet/ToolbarWrapper';
import { ToolbarFlowEnum } from 'components/shared/ToolbarSet/ToolbarWrapper.enums';
import RosterInfoPopover from './RosterInfoPopover';
import { RosterPlanFilters } from './RosterPlanFilters';

// enums
import {
  UserPreferenceKeysEnums,
  UserPreferenceValueEnums
} from '../RosterPlan.enums';

// external libraries
import { useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { startOfWeek } from 'date-fns';

// hooks
import { useDateNavigationStore } from 'components/shared/DateNavigation/DateNavigation.stores';
import { useDateNavigationHoursStore } from 'components/shared/DateNavigation/DateNavigationHours.stores';
import useRosterPlanPreferences from '../hooks/useRosterPlanPreferences';
import useRosterPlanModeAction from '../hooks/useRosterPlanModeAction';
import useRosterPlanMode from '../hooks/useRosterPlanMode';

// stores
import {
  useRosterPlanStore,
  useShowCommitmentCostCenter,
  useShowSortOrderColumn,
  useShowSourceCostCenter
} from '../RosterPlan.stores';

// types
import type React from 'react';
import type { PlanUnitMix } from 'components/types';
import type { SingleOrRange } from 'components/shared/DateNavigation';
import type { ToolbarAction } from 'components/shared/ToolbarSet/ToolbarSet.types';
import type { RosterPlanToolbarProps } from './RosterPlanToolbar.types';

// utils
import { getRosterPlanModeLayout } from '../utils/getRosterPlanModeLayout';
import { toAppliedFilters } from '../utils/toAppliedFilters';

/**
 * The actions that only act on the two grids: changes after publication removes the grids, so it
 * drops these with them rather than leaving controls that change nothing on screen.
 */
const GRID_ACTION_IDS = ['roster-plan-filter', 'roster-plan-view-mode'];

/**
 * The roster screen's toolbar: the period and view actions, the filter popper, the info popover,
 * and the grids, which the toolbar renders as its children.
 *
 * A component rather than a hook (decision 2 in the plan's context): the actions array is JSX-
 * adjacent configuration that only `ToolbarWrapper` consumes, and half of what it needs — popper
 * anchors, open state — is presentation.
 *
 * Every `id` and `actionDataTourId` below is load bearing: the e2e selectors and the
 * FeatureDiscovery tour both key on them.
 */
const RosterPlanToolbar = ({
  startDate,
  endDate,
  filters,
  filterMethods,
  appliedFilters,
  onApplyFilters,
  onClearFilters,
  rosterPublishDate,
  beforeDatePicker,
  children
}: RosterPlanToolbarProps) => {
  'use memo';

  const { t: tCommon } = useTranslation('common');
  const { t: tHeader } = useTranslation('common', { keyPrefix: 'header' });

  const {
    selectedDate,
    selectedDateRange,
    setSelectedDate,
    setSelectedDateRange
  } = useDateNavigationHoursStore((state) => state);
  const { period, setPeriod } = useDateNavigationStore((state) => state);

  const setSelectedPlanUnits = useRosterPlanStore(
    (state) => state.setSelectedPlanUnits
  );

  const showSortOrderColumn = useShowSortOrderColumn();
  const showSourceCostCenter = useShowSourceCostCenter();
  const showCommitmentCostCenter = useShowCommitmentCostCenter();

  const setShowSortOrderColumn = useRosterPlanStore(
    (state) => state.setShowSortOrderColumn
  );
  const setShowSourceCostCenter = useRosterPlanStore(
    (state) => state.setShowSourceCostCenter
  );
  const setShowCommitmentCostCenter = useRosterPlanStore(
    (state) => state.setShowCommitmentCostCenter
  );

  const { saveUserPreferences } = useRosterPlanPreferences();

  const modeAction = useRosterPlanModeAction();
  const { showsPeriodActions, showsRosterGrids } = getRosterPlanModeLayout(
    useRosterPlanMode().mode
  );

  const [anchorElInfo, setAnchorElInfo] = useState<HTMLButtonElement | null>(
    null
  );
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const isInfoOpen = Boolean(anchorElInfo);

  const handleSelectedDateChange = (date: SingleOrRange) => {
    if (period === 'custom') {
      setSelectedDateRange(Array.isArray(date) ? date : undefined);
    } else {
      setSelectedDate(date as Date | null);
    }
  };

  const handlePlanUnitSelected = (planUnits: PlanUnitMix[]) => {
    setSelectedPlanUnits(planUnits);
  };

  const handleApplyFilters = filterMethods.handleSubmit((values) => {
    onApplyFilters(toAppliedFilters(values));
    setIsFilterOpen(false);
  });

  const handleClearFilters = () => {
    onClearFilters();
    setIsFilterOpen(false);
    filterMethods.reset();
  };

  // Changes after publication hides these four, as the Dojo screen did; `today` stays.
  const periodActions: ToolbarAction[] = [
    {
      tooltipLabel: tHeader('day'),
      id: 'roster-plan-day',
      icon: 'calendar-day',
      onClick: () => {
        setSelectedDate(startOfWeek(new Date(), { weekStartsOn: 1 }));
        setPeriod('day');
      },
      actionDataTourId: 'roster-plan-day',
      alignment: 'left'
    },
    {
      tooltipLabel: tHeader('week'),
      id: 'roster-plan-week',
      icon: 'calendar-week',
      onClick: () => {
        setSelectedDate(new Date());
        setPeriod('week');
      },
      actionDataTourId: 'roster-plan-week',
      alignment: 'left'
    },
    {
      tooltipLabel: tHeader('month'),
      id: 'roster-plan-month',
      icon: 'calendar-month',
      onClick: () => {
        setSelectedDate(new Date());
        setPeriod('month');
      },
      actionDataTourId: 'roster-plan-month',
      alignment: 'left'
    },
    {
      tooltipLabel: tHeader('4Weeks'),
      id: 'roster-plan-4Weeks',
      icon: 'calendar', // the plain calendar is the four-week overview; the other three periods take the dedicated day/week/month icons
      onClick: () => {
        setSelectedDate(startOfWeek(new Date(), { weekStartsOn: 1 }));
        setPeriod('fourWeeks');
      },
      rightDivider: true,
      actionDataTourId: 'roster-plan-4Weeks',
      alignment: 'left'
    }
  ];

  const toggleCostCenterColumn = ({
    isVisible,
    setVisible,
    key
  }: {
    isVisible: boolean;
    setVisible: (next: boolean) => void;
    key: UserPreferenceKeysEnums;
  }) => {
    const next = !isVisible;
    setVisible(next);
    saveUserPreferences({
      key,
      value: next
        ? UserPreferenceValueEnums.Show
        : UserPreferenceValueEnums.Hide
    });
  };

  return (
    <ToolbarWrapper
      planUnitSelector={{
        onSelected: handlePlanUnitSelected,
        // The roster plans the displayed period, so its plan unit list has to be scoped to it.
        // On the default interval (today -> tomorrow) a cost centre that only becomes valid later
        // sorts to the bottom and renders as invalid — precisely the one the planner navigated
        // there for. The Dojo roster scoped it the same way: CommonModel appended startDate and
        // endDate from the roster's current interval, which navigation moved.
        interval: { startDate, endDate }
      }}
      actions={(
        [
          {
            tooltipLabel: tHeader('today'),
            id: 'roster-plan-today',
            icon: 'calendar-check',
            onClick: () => {
              setSelectedDate(new Date());
              setPeriod('day');
            },
            actionDataTourId: 'roster-plan-today',
            alignment: 'left'
          },
          ...(showsPeriodActions ? periodActions : []),
          {
            tooltipLabel: tHeader('filter'),
            id: 'roster-plan-filter',
            icon: 'sliders',
            onClick: () => setIsFilterOpen(true),
            rightDivider: true,
            actionDataTourId: 'roster-plan-filter',
            alignment: 'right',
            pulsingBadge: {
              isActive: appliedFilters.length > 0,
              content:
                appliedFilters.length > 0 ? appliedFilters.length : undefined
            }
          },
          {
            tooltipLabel: tHeader('rosterInfo'),
            id: 'roster-plan-info',
            icon: 'circle-info',
            onClick: (event: React.MouseEvent<HTMLButtonElement>) =>
              setAnchorElInfo(event.currentTarget),
            actionDataTourId: 'roster-plan-info',
            alignment: 'right'
          },
          {
            tooltipLabel: tHeader('viewMode'),
            id: 'roster-plan-view-mode',
            icon: 'eye',
            contextMenu: [
              {
                groupTitle: tCommon('toolbar.details'),
                label: tCommon('toolbar.showSourceCostCenter'),
                onClick: () =>
                  toggleCostCenterColumn({
                    isVisible: showSourceCostCenter,
                    setVisible: setShowSourceCostCenter,
                    key: UserPreferenceKeysEnums.RosterEmployeeGridSourceCostCenter
                  }),
                selected: showSourceCostCenter,
                isCheckable: true
              },
              {
                label: tCommon('toolbar.showCommitmentCostCenter'),
                onClick: () =>
                  toggleCostCenterColumn({
                    isVisible: showCommitmentCostCenter,
                    setVisible: setShowCommitmentCostCenter,
                    key: UserPreferenceKeysEnums.RosterEmployeeGridDestinationCostCenter
                  }),
                selected: showCommitmentCostCenter,
                isCheckable: true
              },
              {
                label: tCommon('toolbar.showSortOrderColumn'),
                onClick: () => setShowSortOrderColumn(!showSortOrderColumn),
                selected: showSortOrderColumn,
                isCheckable: true
              }
            ],
            actionDataTourId: 'roster-plan-view-mode',
            alignment: 'right'
          },
          // Last, so it sits just before the toolbar's own menu; see useRosterPlanModeAction.
          modeAction
        ] satisfies ToolbarAction[]
      ).filter(
        (action) => showsRosterGrids || !GRID_ACTION_IDS.includes(action.id)
      )}
      component={{ beforeDatePicker }}
      toolbarFlow={ToolbarFlowEnum.FLEX}
      toolbarDataTourId="roster-plan-header"
      dateNavigation={{
        defaultDate: selectedDate ?? null,
        defaultDateRange: selectedDateRange ?? null,
        options: ['day', 'week', 'fourWeeks', 'month'],
        dataTourId: 'roster-plan-date-navigation',
        allowMenuOptions: true,
        onDateChange: handleSelectedDateChange
      }}
      zenPinned={false} // Todo: update later based on filter open state
    >
      <FormProvider {...filterMethods}>
        <FilterPopper
          isOpen={isFilterOpen}
          filterId="roster-plan-filter"
          getAnchorEl={() => document.getElementById('roster-plan-filter')}
          handleClose={() => setIsFilterOpen(false)}
          handleClearFilters={handleClearFilters}
          handleApplyFilters={handleApplyFilters}
          customZIndex={1200}
        >
          <RosterPlanFilters filters={filters} />
        </FilterPopper>
      </FormProvider>

      <RosterInfoPopover
        id={isInfoOpen ? 'roster-info-popover' : undefined}
        open={isInfoOpen}
        anchorElInfo={anchorElInfo}
        handleInfoClose={() => setAnchorElInfo(null)}
        rosterPublishDate={rosterPublishDate}
      />

      {children}
    </ToolbarWrapper>
  );
};

export default RosterPlanToolbar;
